import "@/App.css";
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Curtain } from "@/components/PageTransition";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { homeFor } from "@/lib/access";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Ledger from "@/pages/Ledger";
import CheckIn from "@/pages/CheckIn";
import CheckInOverview from "@/pages/CheckInOverview";
import StickerDesk from "@/pages/StickerDesk";
import CertificateDispatch from "@/pages/CertificateDispatch";
import VerifyCertificate from "@/pages/VerifyCertificate";
import LabelStudio from "@/pages/LabelStudio";
import Certificates from "@/pages/Certificates";
import AccessControl from "@/pages/AccessControl";
import HomePage from "@/pages/Home";
import ManageEvents from "@/pages/ManageEvents";
import MasterListPage from "@/pages/MasterList";
import EventSetup from "@/pages/EventSetup";
import SiteSettings from "@/pages/SiteSettings";
import Sponsors from "@/pages/Sponsors";
import SpecialReceipts from "@/pages/SpecialReceipts";
import ActivityLog from "@/pages/ActivityLog";
import EventRegistrations from "@/pages/EventRegistrations";
import CashReport from "@/pages/CashReport";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import MemberAuth from "@/pages/MemberAuth";
import MyEventHub from "@/pages/MyEventHub";
import MemberProfile from "@/pages/MemberProfile";
import PublicRegistration from "@/pages/PublicRegistration";
import FormBuilder from "@/pages/FormBuilder";
import AccountNotice from "@/pages/AccountNotice";
import FlaggedDevices from "@/pages/FlaggedDevices";
import RemovedAccounts from "@/pages/RemovedAccounts";
import ContactMessages from "@/pages/ContactMessages";
import MemberAccounts from "@/pages/MemberAccounts";
import MemberAccountDetails from "@/pages/MemberAccountDetails";
import LoginNoticePage from "@/pages/LoginNotice";
import Resources from "@/pages/Resources";
import ResourceAlbum from "@/pages/ResourceAlbum";
import ManageResources from "@/pages/ManageResources";
import { IdleWatcher } from "@/components/IdleWatcher";

const Fallback = () => {
  const { user, auth } = useApp();
  if (auth === null) return null;
  if (auth?.kind === "member") return <Navigate to="/my" replace />;
  return <Navigate to={user ? homeFor(user) : "/"} replace />;
};

const LoginRedirect = () => <Navigate to={`/login${useLocation().search}`} replace />;

const AnimatedRoutes = () => {
  const location = useLocation();
  const { auth } = useApp();
  if (auth?.kind === "member" && auth.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  const key = ["/", "/login", "/change-password", "/reset-password", "/event-registration", "/member/login", "/member/verify", "/my", "/my/profile", "/resources"].includes(location.pathname) ? location.pathname : "shell";
  return (
    <AnimatePresence mode="wait">
      <Curtain key={key}>
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<MemberAuth />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/event-registration" element={<PublicRegistration />} />
          <Route path="/member/login" element={<LoginRedirect />} />
          <Route path="/member/verify" element={<VerifyEmail />} />
          <Route path="/my" element={<MyEventHub />} />
          <Route path="/verify-certificate/:token" element={<VerifyCertificate />} />
          <Route path="/my/profile" element={<MemberProfile />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/gallery/:id" element={<ResourceAlbum />} />
          <Route element={<AppShell />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/register" element={<Navigate to="/event-registrations" replace />} />
            <Route path="/ledger" element={<Ledger />} />
            <Route path="/cash-report" element={<CashReport />} />
            <Route path="/checkin" element={<CheckIn />} />
            <Route path="/checkin-overview" element={<CheckInOverview />} />
            <Route path="/certificate-dispatch" element={<CertificateDispatch />} />
            <Route path="/sticker-desk" element={<StickerDesk />} />
            <Route path="/labels" element={<LabelStudio />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/access" element={<AccessControl />} />
            <Route path="/manage-events" element={<ManageEvents />} />
            <Route path="/manage-events/new" element={<EventSetup />} />
            <Route path="/manage-events/:id" element={<EventSetup />} />
            <Route path="/master-list" element={<MasterListPage />} />
            <Route path="/member-accounts" element={<MemberAccounts />} />
            <Route path="/member-accounts/:id" element={<MemberAccountDetails />} />
            <Route path="/site-settings" element={<SiteSettings />} />
            <Route path="/sponsors" element={<Sponsors />} />
            <Route path="/special-receipts" element={<SpecialReceipts />} />
            <Route path="/activity" element={<ActivityLog />} />
            <Route path="/event-registrations" element={<EventRegistrations />} />
            <Route path="/form-builder" element={<FormBuilder />} />
            <Route path="/account-notice" element={<AccountNotice />} />
            <Route path="/login-notice" element={<LoginNoticePage />} />
            <Route path="/flagged-devices" element={<FlaggedDevices />} />
            <Route path="/removed-accounts" element={<RemovedAccounts />} />
            <Route path="/contact-messages" element={<ContactMessages />} />
            <Route path="/manage-resources" element={<ManageResources />} />
          </Route>
          <Route path="*" element={<Fallback />} />
        </Routes>
      </Curtain>
    </AnimatePresence>
  );
};

const router = createBrowserRouter([{ path: "*", element: <><IdleWatcher /><AnimatedRoutes /></> }]);

function App() {
  return (
    <AppProvider>
      <RouterProvider router={router} />
      <Toaster />
    </AppProvider>
  );
}

export default App;
